// src/pages/VoorschotBeheer.js - Updated with Email Generator Tab
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import '../css/VoorschotBeheer.css';
import '../css/VoorschotAnalysis.css';
import BeheerdersportaalSidebar from '../components/BeheerdersportaalSidebar';
import VoorschotAnalysis from '../components/VoorschotAnalysis';
import VoorschotEmailGenerator from '../components/VoorschotEmailGenerator'; // Add this import
import api from '../components/api';

const VoorschotBeheer = () => {
  const [apartments, setApartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedEnergyYear, setSelectedEnergyYear] = useState(() => {
    // Initialize with current energy year
    const currentDate = new Date();
    const currentMonth = currentDate.getMonth() + 1;
    const currentCalendarYear = currentDate.getFullYear();
    const currentEnergyYearStart = currentMonth >= 7 ? currentCalendarYear : currentCalendarYear - 1;
    return `${currentEnergyYearStart}-${currentEnergyYearStart + 1}`;
  });
  const [summary, setSummary] = useState(null);
  const [bulkUpdateMode, setBulkUpdateMode] = useState(false);
  const [selectedApartments, setSelectedApartments] = useState(new Set());
  const [bulkAmount, setBulkAmount] = useState('');
  const [bulkStartMonth, setBulkStartMonth] = useState(0); // July (index 0)
  const [bulkEndMonth, setBulkEndMonth] = useState(11); // June (index 11)
  const [viewMode, setViewMode] = useState('yearly'); // 'yearly' or 'monthly'
  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [activeTab, setActiveTab] = useState('overview'); // Updated to include 'emails' tab
  
  // Collapsible sections state
  const [collapsedSections, setCollapsedSections] = useState({
    Toren: true,
    Kraag: true
  });
  
  // Settings panel state
  const [showSettings, setShowSettings] = useState(false);
  
  // Bulk update state
  const [bulkUpdateInProgress, setBulkUpdateInProgress] = useState(false);
  const [bulkUpdateResults, setBulkUpdateResults] = useState(null);
  const [showBulkResults, setShowBulkResults] = useState(false);
  
  // OPTIMIZATION: Add cache for apartment data
  const [apartmentCache, setApartmentCache] = useState(new Map());
  const [lastFetchTime, setLastFetchTime] = useState(new Map());
  
  // OPTIMIZATION: Add loading states for individual operations
  const [individualLoading, setIndividualLoading] = useState(new Set());
  
  const navigate = useNavigate();

  // Generate energy year options (from 2023-2024 to current energy year + 2)
  const generateEnergyYearOptions = useMemo(() => {
    const currentDate = new Date();
    const currentMonth = currentDate.getMonth() + 1; // 1-12
    const currentCalendarYear = currentDate.getFullYear();
    
    // Determine current energy year
    const currentEnergyYearStart = currentMonth >= 7 ? currentCalendarYear : currentCalendarYear - 1;
    
    const energyYears = [];
    // Start from 2023-2024 energy year, go to current + 2
    for (let startYear = 2023; startYear <= currentEnergyYearStart + 2; startYear++) {
      energyYears.push(`${startYear}-${startYear + 1}`);
    }
    return energyYears;
  }, []);

  // Month names in energy year order (July to June)
  const monthNames = useMemo(() => [
    'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dec',
    'Jan', 'Feb', 'Mrt', 'Apr', 'Mei', 'Jun'
  ], []);

  const fullMonthNames = useMemo(() => [
    'Juli', 'Augustus', 'September', 'Oktober', 'November', 'December',
    'Januari', 'Februari', 'Maart', 'April', 'Mei', 'Juni'
  ], []);

  // OPTIMIZATION: Debounce energy year changes
  const [pendingEnergyYear, setPendingEnergyYear] = useState(null);
  
  useEffect(() => {
    if (pendingEnergyYear && pendingEnergyYear !== selectedEnergyYear) {
      const timer = setTimeout(() => {
        setSelectedEnergyYear(pendingEnergyYear);
        setPendingEnergyYear(null);
      }, 300); // 300ms debounce
      
      return () => clearTimeout(timer);
    }
  }, [pendingEnergyYear, selectedEnergyYear]);

  // Toggle section collapse
  const toggleSectionCollapse = useCallback((sectionName) => {
    setCollapsedSections(prev => ({
      ...prev,
      [sectionName]: !prev[sectionName]
    }));
  }, []);

  // Toggle all sections
  const toggleAllSections = useCallback(() => {
    const anyExpanded = Object.values(collapsedSections).some(collapsed => !collapsed);
    const newState = anyExpanded; // If any expanded, collapse all. If all collapsed, expand all.
    
    setCollapsedSections({
      Toren: newState,
      Kraag: newState
    });
  }, [collapsedSections]);

  // Map energy year month index to calendar month
  const getCalendarMonth = useCallback((energyMonthIndex) => {
    // Energy year: Jul(0) Aug(1) Sep(2) Oct(3) Nov(4) Dec(5) Jan(6) Feb(7) Mar(8) Apr(9) May(10) Jun(11)
    // Calendar:   Jul(7) Aug(8) Sep(9) Oct(10) Nov(11) Dec(12) Jan(1) Feb(2) Mar(3) Apr(4) May(5) Jun(6)
    return energyMonthIndex < 6 ? energyMonthIndex + 7 : energyMonthIndex - 5;
  }, []);

  // Get the calendar year for a specific energy year and month
  const getCalendarYear = useCallback((energyYear, energyMonthIndex) => {
    const startYear = parseInt(energyYear.split('-')[0]);
    return energyMonthIndex < 6 ? startYear : startYear + 1;
  }, []);

  // OPTIMIZATION: Improved cache-based data fetching
  const fetchEnergyYearVoorschotten = useCallback(async (forceRefresh = false) => {
    try {
      const cacheKey = selectedEnergyYear;
      const now = Date.now();
      const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
      
      // Check cache first (unless force refresh)
      if (!forceRefresh && apartmentCache.has(cacheKey)) {
        const lastFetch = lastFetchTime.get(cacheKey) || 0;
        if (now - lastFetch < CACHE_DURATION) {
          console.log(`📋 Using cached data for ${selectedEnergyYear}`);
          const cachedData = apartmentCache.get(cacheKey);
          setApartments(cachedData.apartments);
          setSummary(cachedData.summary);
          setLoading(false);
          return;
        }
      }
      
      setLoading(true);
      setError(null);
      
      console.log(`📋 Fetching fresh data for ${selectedEnergyYear}`);
      
      // OPTIMIZATION: Use single optimized endpoint
      const response = await api.get(`/api/voorschotten/energy-year/${selectedEnergyYear}`);
      
      if (response.data) {
        const { apartments: apartmentData, summary: summaryData } = response.data;
        
        // Cache the data
        apartmentCache.set(cacheKey, { apartments: apartmentData, summary: summaryData });
        lastFetchTime.set(cacheKey, now);
        
        setApartments(apartmentData);
        setSummary(summaryData);
      } else {
        throw new Error('No data received from server');
      }
      
    } catch (err) {
      console.error('❌ Error fetching energy year voorschotten:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Unknown error';
      setError(`Kon voorschotten niet laden: ${errorMessage}`);
      setApartments([]);
      setSummary(null);
    } finally {
      setLoading(false);
    }
  }, [selectedEnergyYear, apartmentCache, lastFetchTime]);

  // OPTIMIZATION: Local state update for immediate UI feedback
  const updateApartmentLocal = useCallback((apartmentNumber, energyMonthIndex, newAmount) => {
    setApartments(prev => prev.map(apt => {
      if (apt.apartmentNumber === apartmentNumber) {
        const calendarMonth = getCalendarMonth(energyMonthIndex);
        const calendarYear = getCalendarYear(selectedEnergyYear, energyMonthIndex);
        
        let updatedEnergyYear = apt.energyYearVoorschotten || [];
        
        const existingIndex = updatedEnergyYear.findIndex(mv => 
          mv.month === calendarMonth && mv.year === calendarYear
        );
        
        if (existingIndex >= 0) {
          updatedEnergyYear[existingIndex] = {
            ...updatedEnergyYear[existingIndex],
            amount: newAmount,
            lastUpdated: new Date()
          };
        } else {
          updatedEnergyYear.push({
            year: calendarYear,
            month: calendarMonth,
            amount: newAmount,
            notes: `Updated ${new Date().toLocaleDateString('nl-NL')}`,
            lastUpdated: new Date(),
            updatedBy: 'admin'
          });
        }
        
        return { ...apt, energyYearVoorschotten: updatedEnergyYear };
      }
      return apt;
    }));
    
    // Update cache
    const cacheKey = selectedEnergyYear;
    if (apartmentCache.has(cacheKey)) {
      const cached = apartmentCache.get(cacheKey);
      cached.apartments = apartments; // This will be updated by the state change above
      apartmentCache.set(cacheKey, cached);
    }
  }, [getCalendarMonth, getCalendarYear, selectedEnergyYear, apartmentCache, apartments]);

  // OPTIMIZATION: Optimized bulk update with progress tracking
  const applyBulkUpdate = useCallback(async () => {
    if (selectedApartments.size === 0) {
      alert('Selecteer eerst appartementen om bij te werken');
      return;
    }

    const amount = parseFloat(bulkAmount);
    if (!bulkAmount || isNaN(amount) || amount < 0) {
      alert('Voer een geldig bedrag in (minimaal €0.00)');
      return;
    }

    if (bulkStartMonth > bulkEndMonth) {
      alert('Start maand kan niet na eind maand zijn');
      return;
    }

    const apartmentList = Array.from(selectedApartments);
    const monthCount = bulkEndMonth - bulkStartMonth + 1;
    const totalUpdates = apartmentList.length * monthCount;
    
    if (!window.confirm(
      `${apartmentList.length} appartementen bijwerken met €${amount.toFixed(2)} ` +
      `voor ${monthCount} maanden (${fullMonthNames[bulkStartMonth]} t/m ${fullMonthNames[bulkEndMonth]}) ${selectedEnergyYear}?\n\n` +
      `Totaal ${totalUpdates} updates worden uitgevoerd.`
    )) {
      return;
    }

    setBulkUpdateInProgress(true);
    setBulkUpdateResults(null);
    setShowBulkResults(false);

    try {
      console.log(`🔄 Starting optimized bulk update for ${apartmentList.length} apartments`);
      
      // OPTIMIZATION: Prepare all updates first
      const updates = [];
      for (const apartmentNumber of apartmentList) {
        for (let energyMonthIndex = bulkStartMonth; energyMonthIndex <= bulkEndMonth; energyMonthIndex++) {
          const calendarMonth = getCalendarMonth(energyMonthIndex);
          const calendarYear = getCalendarYear(selectedEnergyYear, energyMonthIndex);
          
          updates.push({
            apartmentNumber,
            year: calendarYear,
            month: calendarMonth,
            amount: amount,
            notes: `Bulk update ${new Date().toLocaleDateString('nl-NL')}`
          });
        }
      }
      
      // OPTIMIZATION: Use bulk update endpoint
      const response = await api.post('/api/voorschotten/bulk-update-optimized', {
        updates,
        year: selectedEnergyYear.split('-')[0],
        month: 1 // Not used in optimized version
      });
      
      setBulkUpdateResults(response.data.results);
      setShowBulkResults(true);

      const successRate = Math.round((response.data.results.successful.length / response.data.results.total) * 100);
      const message = `Bulk update voltooid!\n\n` +
        `✅ Succesvol: ${response.data.results.successful.length}/${response.data.results.total} (${successRate}%)\n` +
        `❌ Mislukt: ${response.data.results.failed.length}`;
      
      if (response.data.results.failed.length > 0) {
        alert(message + '\n\nKlik op "Bulk Update Resultaten" voor details.');
      } else {
        alert(message);
      }

      // OPTIMIZATION: Only refresh if we had successful updates
      if (response.data.results.successful.length > 0) {
        setBulkUpdateMode(false);
        setSelectedApartments(new Set());
        setBulkAmount('');
        
        // Invalidate cache and refresh
        apartmentCache.delete(selectedEnergyYear);
        await fetchEnergyYearVoorschotten(true);
      }

    } catch (err) {
      console.error('❌ Bulk update process failed:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Unknown error';
      alert(`Bulk update mislukt: ${errorMessage}`);
    } finally {
      setBulkUpdateInProgress(false);
    }
  }, [
    selectedApartments, bulkAmount, bulkStartMonth, bulkEndMonth, fullMonthNames, 
    selectedEnergyYear, getCalendarMonth, getCalendarYear, apartmentCache, 
    fetchEnergyYearVoorschotten
  ]);

  // OPTIMIZATION: Optimized cell edit with immediate UI feedback
  const saveCellEdit = useCallback(async (apartmentNumber, energyMonthIndex) => {
    const amount = parseFloat(editValue);
    if (isNaN(amount) || amount < 0) {
      alert('Voer een geldig bedrag in (minimaal €0.00)');
      return;
    }
  
    const calendarMonth = getCalendarMonth(energyMonthIndex);
    const calendarYear = getCalendarYear(selectedEnergyYear, energyMonthIndex);
    const cellKey = `${apartmentNumber}-${energyMonthIndex}`;
  
    try {
      // OPTIMIZATION: Update UI immediately for responsive feel
      updateApartmentLocal(apartmentNumber, energyMonthIndex, amount);
      setEditingCell(null);
      setEditValue('');
      
      // Add to loading set
      setIndividualLoading(prev => new Set(prev).add(cellKey));
      
      console.log(`💾 Saving cell edit: ${apartmentNumber} ${calendarYear}-${calendarMonth} = €${amount}`);
      
      // OPTIMIZATION: Fire and forget for single updates, with error handling
      api.put(`/api/voorschotten/${apartmentNumber}/month`, {
        year: calendarYear,
        month: calendarMonth,
        amount: amount,
        notes: `Updated ${new Date().toLocaleDateString('nl-NL')}`
      }).then(() => {
        console.log(`✅ Successfully saved ${apartmentNumber}`);
        // Remove from loading set
        setIndividualLoading(prev => {
          const newSet = new Set(prev);
          newSet.delete(cellKey);
          return newSet;
        });
      }).catch(err => {
        console.error('❌ Error saving cell edit:', err);
        // Revert the UI change on error
        const apartment = apartments.find(apt => apt.apartmentNumber === apartmentNumber);
        if (apartment) {
          const originalAmount = getAmountForEnergyMonth(apartment, energyMonthIndex);
          updateApartmentLocal(apartmentNumber, energyMonthIndex, originalAmount);
        }
        
        // Remove from loading set
        setIndividualLoading(prev => {
          const newSet = new Set(prev);
          newSet.delete(cellKey);
          return newSet;
        });
        
        const errorMessage = err.response?.data?.message || err.message || 'Unknown error';
        alert(`Fout bij opslaan: ${errorMessage}`);
      });
      
    } catch (err) {
      console.error('❌ Error in saveCellEdit:', err);
      alert(`Fout bij opslaan: ${err.message}`);
      setIndividualLoading(prev => {
        const newSet = new Set(prev);
        newSet.delete(cellKey);
        return newSet;
      });
    }
  }, [
    editValue, getCalendarMonth, getCalendarYear, selectedEnergyYear, 
    updateApartmentLocal, apartments
  ]);

  // Initialize the voorschotten system
  const handleInitialize = useCallback(async () => {
    if (!window.confirm('Dit zal alle appartementen initialiseren met een standaard voorschot van €60. Doorgaan?')) {
      return;
    }

    try {
      setLoading(true);
      const response = await api.post('/api/voorschotten/initialize', {
        defaultAmount: 60
      });
      
      alert('Voorschotten systeem succesvol geïnitialiseerd!\n' + 
            `${response.data.summary.created} nieuwe entries aangemaakt\n` +
            `${response.data.summary.updated} entries bijgewerkt`);
      
      // Clear cache and refresh
      apartmentCache.clear();
      lastFetchTime.clear();
      await fetchEnergyYearVoorschotten(true);
      setShowSettings(false);
    } catch (err) {
      console.error('Error initializing:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Unknown error';
      alert(`Fout bij initialiseren: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  }, [apartmentCache, lastFetchTime, fetchEnergyYearVoorschotten]);

  // Toggle apartment selection for bulk operations
  const toggleApartmentSelection = useCallback((apartmentNumber) => {
    setSelectedApartments(prev => {
      const newSelection = new Set(prev);
      if (newSelection.has(apartmentNumber)) {
        newSelection.delete(apartmentNumber);
      } else {
        newSelection.add(apartmentNumber);
      }
      return newSelection;
    });
  }, []);

  // Select all apartments
  const selectAllApartments = useCallback(() => {
    setSelectedApartments(prev => {
      if (prev.size === apartments.length) {
        return new Set();
      } else {
        return new Set(apartments.map(apt => apt.apartmentNumber));
      }
    });
  }, [apartments]);

  // Start editing a cell with validation
  const startEditing = useCallback((apartmentNumber, energyMonthIndex) => {
    if (bulkUpdateMode || bulkUpdateInProgress) {
      return; // Don't allow editing during bulk operations
    }
    
    const apartment = apartments.find(apt => apt.apartmentNumber === apartmentNumber);
    if (!apartment) {
      console.warn(`Apartment ${apartmentNumber} not found`);
      return;
    }
    
    const calendarMonth = getCalendarMonth(energyMonthIndex);
    const calendarYear = getCalendarYear(selectedEnergyYear, energyMonthIndex);
    
    const monthData = apartment.energyYearVoorschotten?.find(mv => 
      mv.month === calendarMonth && mv.year === calendarYear
    );
    const currentValue = monthData?.amount || apartment.defaultMonthlyAmount || 0;
    
    setEditingCell(`${apartmentNumber}-${energyMonthIndex}`);
    setEditValue(currentValue.toString());
  }, [
    bulkUpdateMode, bulkUpdateInProgress, apartments, getCalendarMonth, 
    getCalendarYear, selectedEnergyYear
  ]);

  // Cancel cell edit
  const cancelCellEdit = useCallback(() => {
    setEditingCell(null);
    setEditValue('');
  }, []);

  // Get amount for a specific apartment and energy year month with fallback
  const getAmountForEnergyMonth = useCallback((apartment, energyMonthIndex) => {
    if (!apartment) {
      console.warn('getAmountForEnergyMonth: apartment is null/undefined');
      return 0;
    }
    
    try {
      const calendarMonth = getCalendarMonth(energyMonthIndex);
      const calendarYear = getCalendarYear(selectedEnergyYear, energyMonthIndex);
      
      // Look for month data in energyYearVoorschotten
      const monthData = apartment.energyYearVoorschotten?.find(mv => 
        mv.month === calendarMonth && mv.year === calendarYear
      );
      
      if (monthData && typeof monthData.amount === 'number') {
        return monthData.amount;
      }
      
      // Fallback to default amount
      const defaultAmount = apartment.defaultMonthlyAmount || 0;
      return defaultAmount;
      
    } catch (error) {
      console.error(`Error getting amount for ${apartment.apartmentNumber} month ${energyMonthIndex}:`, error);
      return 0;
    }
  }, [getCalendarMonth, getCalendarYear, selectedEnergyYear]);

  // OPTIMIZATION: Load data when component mounts or when energy year changes (only for overview tab)
  useEffect(() => {
    if (activeTab === 'overview') {
      fetchEnergyYearVoorschotten();
    }
  }, [fetchEnergyYearVoorschotten, activeTab]);

  // OPTIMIZATION: Memoized grouped apartments
  const groupedApartments = useMemo(() => {
    const groups = apartments.reduce((groups, apt) => {
      const section = apt.buildingSection || 'Onbekend';
      if (!groups[section]) {
        groups[section] = [];
      }
      groups[section].push(apt);
      return groups;
    }, {});

    // Sort apartments within each section
    Object.keys(groups).forEach(section => {
      groups[section].sort((a, b) => {
        const aNum = a.apartmentNumber;
        const bNum = b.apartmentNumber;
        
        if (!isNaN(aNum) && !isNaN(bNum)) {
          return parseInt(aNum) - parseInt(bNum);
        }
        
        return aNum.localeCompare(bNum, undefined, { numeric: true });
      });
    });

    return groups;
  }, [apartments]);

  // OPTIMIZATION: Handle energy year change with debouncing
  const handleEnergyYearChange = useCallback((newEnergyYear) => {
    setPendingEnergyYear(newEnergyYear);
  }, []);

  if (loading && apartments.length === 0 && activeTab === 'overview') {
    return (
      <div className="beheer-wrapper">
        <BeheerdersportaalSidebar>
          <main className="dashboard">
            <h1>💶 Voorschotten Beheer</h1>
            <div className="loading-indicator">
              <div className="spinner"></div>
              <p>Voorschotten laden...</p>
            </div>
          </main>
        </BeheerdersportaalSidebar>
      </div>
    );
  }

  return (
    <div className="beheer-wrapper">
      <BeheerdersportaalSidebar>
        <main className="dashboard">
          <h1>💶 Voorschotten Beheer</h1>
          
          {error && (
            <div className="error-banner">
              <p>{error}</p>
              <button onClick={() => setError(null)}>✕</button>
            </div>
          )}

          {/* Tab Navigation */}
          <div className="tab-navigation">
            <button 
              className={`tab-button ${activeTab === 'overview' ? 'active' : ''}`}
              onClick={() => setActiveTab('overview')}
            >
              📊 Overzicht Voorschotten
            </button>
            <button 
              className={`tab-button ${activeTab === 'analysis' ? 'active' : ''}`}
              onClick={() => setActiveTab('analysis')}
            >
              🎯 Kostenvergelijking (Toren)
            </button>
            <button 
              className={`tab-button ${activeTab === 'emails' ? 'active' : ''}`}
              onClick={() => setActiveTab('emails')}
            >
              📧 Gasverbruik E-mails
            </button>
          </div>

          {/* Overview Tab Content */}
          {activeTab === 'overview' && (
            <>
              {/* Controls */}
              <div className="voorschot-controls">
                <div className="period-selector">
                  <label>
                    Energiejaar:
                    <select 
                      value={pendingEnergyYear || selectedEnergyYear} 
                      onChange={(e) => handleEnergyYearChange(e.target.value)}
                      disabled={loading || bulkUpdateInProgress}
                    >
                      {generateEnergyYearOptions.map(energyYear => (
                        <option key={energyYear} value={energyYear}>{energyYear}</option>
                      ))}
                    </select>
                    {pendingEnergyYear && (
                      <span className="loading-indicator small">Laden...</span>
                    )}
                  </label>
                </div>

                <div className="action-buttons">
                  <button 
                    onClick={() => setBulkUpdateMode(!bulkUpdateMode)}
                    className={`bulk-button ${bulkUpdateMode ? 'active' : ''}`}
                    disabled={loading || bulkUpdateInProgress}
                  >
                    {bulkUpdateMode ? '❌ Annuleer Bulk' : '📝 Bulk Bewerken'}
                  </button>
                  
                  <button 
                    onClick={toggleAllSections}
                    className="collapse-button"
                    disabled={loading || bulkUpdateInProgress}
                    title="Klap alle secties in/uit"
                  >
                    {Object.values(collapsedSections).every(collapsed => collapsed) ? '📂 Alles Uitklappen' : '📁 Alles Inklappen'}
                  </button>
                  
                  <button 
                    onClick={() => setShowSettings(!showSettings)}
                    className="settings-button"
                    disabled={loading || bulkUpdateInProgress}
                  >
                    ⚙️ Instellingen
                  </button>
                </div>
              </div>

              {/* Settings Panel */}
              {showSettings && (
                <div className="settings-panel">
                  <h3>⚙️ Systeem Instellingen</h3>
                  <div className="settings-content">
                    <div className="setting-item">
                      <h4>Systeem Initialiseren</h4>
                      <p>Maak voorschot entries aan voor alle appartementen met standaard bedrag van €60.</p>
                      <button 
                        onClick={handleInitialize}
                        className="initialize-button"
                        disabled={loading}
                      >
                        🚀 Systeem Initialiseren
                      </button>
                    </div>
                    
                    <div className="setting-item">
                      <h4>Data Status</h4>
                      <p>Totaal appartementen: {apartments.length}</p>
                      <p>Energiejaar: {selectedEnergyYear}</p>
                      {summary && (
                        <p>Totaal budget: €{summary.grandTotal.toLocaleString('nl-NL')}</p>
                      )}
                    </div>
                  </div>
                  
                  <button 
                    onClick={() => setShowSettings(false)}
                    className="close-settings-button"
                  >
                    Sluiten
                  </button>
                </div>
              )}

              {/* Summary */}
              {summary && (
                <div className="summary-card">
                  <h3>Overzicht Energiejaar {selectedEnergyYear}</h3>
                  <div className="summary-stats">
                    <div className="stat total-stat">
                      <span className="stat-label">Totaal Appartementen:</span>
                      <span className="stat-value">{summary.totalApartments}</span>
                    </div>
                    <div className="stat total-stat">
                      <span className="stat-label">Totaal Jaar:</span>
                      <span className="stat-value">€{summary.grandTotal.toLocaleString('nl-NL')}</span>
                    </div>
                    <div className="stat total-stat">
                      <span className="stat-label">Per Maand Gemiddeld:</span>
                      <span className="stat-value">€{Math.round(summary.grandTotal / 12).toLocaleString('nl-NL')}</span>
                    </div>
                  </div>
                  
                  {summary.sectionBreakdown && (
                    <div className="section-breakdown">
                      <h4>Uitsplitsing per Gebouwdeel</h4>
                      <div className="breakdown-grid">
                        {Object.entries(summary.sectionBreakdown).map(([section, data]) => (
                          <div key={section} className={`breakdown-section ${section.toLowerCase()}`}>
                            <h5>{section}</h5>
                            <div className="breakdown-stats">
                              <div className="breakdown-stat">
                                <span className="breakdown-label">Appartementen:</span>
                                <span className="breakdown-value">{data.count}</span>
                              </div>
                              <div className="breakdown-stat">
                                <span className="breakdown-label">Totaal Jaar:</span>
                                <span className="breakdown-value">€{data.total.toLocaleString('nl-NL')}</span>
                              </div>
                              <div className="breakdown-stat">
                                <span className="breakdown-label">Per Maand:</span>
                                <span className="breakdown-value">€{Math.round(data.total / 12).toLocaleString('nl-NL')}</span>
                              </div>
                              <div className="breakdown-stat">
                                <span className="breakdown-label">Per App/Jaar:</span>
                                <span className="breakdown-value">€{Math.round(data.total / data.count).toLocaleString('nl-NL')}</span>
                              </div>
                              <div className="breakdown-stat">
                                <span className="breakdown-label">Per App/Maand:</span>
                                <span className="breakdown-value">€{Math.round(data.total / data.count / 12).toLocaleString('nl-NL')}</span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Bulk Update Panel */}
              {bulkUpdateMode && (
                <div className="bulk-update-panel">
                  <h3>Bulk Bewerken</h3>
                  
                  {/* Progress indicator */}
                  {bulkUpdateInProgress && (
                    <div className="bulk-update-progress">
                      <div className="spinner"></div>
                      <span>Bezig met bulk update...</span>
                    </div>
                  )}
                  
                  <div className="bulk-controls">
                    <div className="bulk-selection">
                      <button 
                        onClick={selectAllApartments}
                        className="select-all-button"
                        disabled={bulkUpdateInProgress}
                      >
                        {selectedApartments.size === apartments.length ? 'Deselecteer Alles' : 'Selecteer Alles'}
                      </button>
                      <span className="selection-count">
                        {selectedApartments.size} van {apartments.length} geselecteerd
                      </span>
                    </div>
                    
                    <div className="bulk-settings">
                      <label>
                        Bedrag (€):
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={bulkAmount}
                          onChange={(e) => setBulkAmount(e.target.value)}
                          placeholder="60.00"
                          disabled={bulkUpdateInProgress}
                        />
                      </label>
                      
                      <label>
                        Van Maand:
                        <select 
                          value={bulkStartMonth} 
                          onChange={(e) => setBulkStartMonth(parseInt(e.target.value))}
                          disabled={bulkUpdateInProgress}
                        >
                          {fullMonthNames.map((name, index) => (
                            <option key={index} value={index}>{name}</option>
                          ))}
                        </select>
                      </label>
                      
                      <label>
                        Tot Maand:
                        <select 
                          value={bulkEndMonth} 
                          onChange={(e) => setBulkEndMonth(parseInt(e.target.value))}
                          disabled={bulkUpdateInProgress}
                        >
                          {fullMonthNames.map((name, index) => (
                            <option key={index} value={index}>{name}</option>
                          ))}
                        </select>
                      </label>
                      
                      <button 
                        onClick={applyBulkUpdate}
                        className="apply-bulk-button"
                        disabled={loading || bulkUpdateInProgress || selectedApartments.size === 0 || !bulkAmount}
                      >
                        {bulkUpdateInProgress ? '⏳ Bezig...' : '✅ Toepassen'}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Apartments Table */}
              <div className="apartments-table-container">
                <table className="apartments-table">
                  <thead>
                    <tr>
                      {bulkUpdateMode && <th className="checkbox-col">
                        <input
                          type="checkbox"
                          checked={selectedApartments.size === apartments.length && apartments.length > 0}
                          onChange={selectAllApartments}
                          disabled={bulkUpdateInProgress}
                        />
                      </th>}
                      <th className="apartment-col">App.</th>
                      <th className="size-col">Grootte</th>
                      {monthNames.map((month, energyMonthIndex) => (
                        <th key={energyMonthIndex} className="month-col">
                          {month}
                        </th>
                      ))}
                      <th className="total-col">Totaal</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.keys(groupedApartments).map(section => (
                      <React.Fragment key={section}>
                        <tr className="section-header">
                          <td colSpan={bulkUpdateMode ? 16 : 15} className="section-title">
                            <div className="section-header-content">
                              <button
                                className="section-toggle-button"
                                onClick={() => toggleSectionCollapse(section)}
                                disabled={loading || bulkUpdateInProgress}
                              >
                                {collapsedSections[section] ? '📂' : '📁'}
                              </button>
                              <span className="section-name">
                                {section} ({groupedApartments[section].length} appartementen)
                              </span>
                            </div>
                          </td>
                        </tr>
                        {!collapsedSections[section] && groupedApartments[section].map(apartment => {
                          const energyYearTotal = monthNames.reduce((total, _, energyMonthIndex) => {
                            return total + getAmountForEnergyMonth(apartment, energyMonthIndex);
                          }, 0);
                          
                          return (
                            <tr key={apartment.apartmentNumber} className="apartment-row">
                              {bulkUpdateMode && (
                                <td className="checkbox-cell">
                                  <input
                                    type="checkbox"
                                    checked={selectedApartments.has(apartment.apartmentNumber)}
                                    onChange={() => toggleApartmentSelection(apartment.apartmentNumber)}
                                    disabled={bulkUpdateInProgress}
                                  />
                                </td>
                              )}
                              <td className="apartment-cell">
                                <strong>{apartment.apartmentNumber}</strong>
                                {apartment.currentResident?.name && (
                                  <div className="resident-name">{apartment.currentResident.name}</div>
                                )}
                              </td>
                              <td className="size-cell">
                                <span className={`size-badge ${apartment.apartmentSize?.toLowerCase()}`}>
                                  {apartment.apartmentSize === 'SMALL' ? 'Klein' : 'Groot'}
                                </span>
                              </td>
                              {monthNames.map((_, energyMonthIndex) => {
                                const amount = getAmountForEnergyMonth(apartment, energyMonthIndex);
                                const cellKey = `${apartment.apartmentNumber}-${energyMonthIndex}`;
                                const isEditing = editingCell === cellKey;
                                const isLoading = individualLoading.has(cellKey);
                                
                                return (
                                  <td key={energyMonthIndex} className="amount-cell">
                                    {isEditing ? (
                                      <div className="cell-editor">
                                        <input
                                          type="number"
                                          step="0.01"
                                          min="0"
                                          value={editValue}
                                          onChange={(e) => setEditValue(e.target.value)}
                                          onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                              e.preventDefault();
                                              saveCellEdit(apartment.apartmentNumber, energyMonthIndex);
                                            } else if (e.key === 'Escape') {
                                              e.preventDefault();
                                              cancelCellEdit();
                                            }
                                          }}
                                          onBlur={() => saveCellEdit(apartment.apartmentNumber, energyMonthIndex)}
                                          autoFocus
                                        />
                                      </div>
                                    ) : (
                                      <div 
                                        className={`amount-display ${!bulkUpdateMode ? 'editable' : ''} ${isLoading ? 'loading' : ''}`}
                                        onClick={() => !bulkUpdateMode && startEditing(apartment.apartmentNumber, energyMonthIndex)}
                                        title={!bulkUpdateMode ? 'Klik om te bewerken' : ''}
                                      >
                                        €{amount.toFixed(2)}
                                        {isLoading && <span className="micro-spinner"></span>}
                                      </div>
                                    )}
                                  </td>
                                );
                              })}
                              <td className="total-cell">
                                <strong>€{energyYearTotal.toFixed(2)}</strong>
                              </td>
                            </tr>
                          );
                        })}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>

              {apartments.length === 0 && !loading && (
                <div className="empty-state">
                  <h3>Geen voorschotten gevonden</h3>
                  <p>Klik op "Instellingen" en dan "Systeem Initialiseren" om te beginnen.</p>
                  <button 
                    onClick={() => setShowSettings(true)}
                    className="settings-button"
                  >
                    ⚙️ Open Instellingen
                  </button>
                </div>
              )}
            </>
          )}

          {/* Analysis Tab Content */}
          {activeTab === 'analysis' && (
            <VoorschotAnalysis selectedEnergyYear={selectedEnergyYear} />
          )}

          {/* Email Generator Tab Content */}
          {activeTab === 'emails' && (
            <VoorschotEmailGenerator selectedEnergyYear={selectedEnergyYear} />
          )}

        </main>
      </BeheerdersportaalSidebar>
    </div>
  );
};

export default VoorschotBeheer;